import { useState } from 'react'
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog"
import { Button } from "@/components/ui/button"
import { Label } from "@/components/ui/label"
import {
  CheckCircle,
  XCircle,
  DollarSign,
  Calculator,
  Shield,
  Users,
  Clock
} from "lucide-react"

interface PayrollPeriod {
  id: string
  periodName: string
  payDate: string
  status: string
  totalGrossPay: number
  totalNetPay: number
  totalTaxes: number
  totalDeductions: number
  _count?: {
    payrollRecords: number
  }
}

interface PayrollApprovalModalProps {
  isOpen: boolean
  onClose: () => void
  period: PayrollPeriod | null
  onApprove: (period: PayrollPeriod) => void
  onReject: (period: PayrollPeriod) => void
}

export function PayrollApprovalModal({
  isOpen,
  onClose,
  period,
  onApprove,
  onReject
}: PayrollApprovalModalProps) {
  const [approvalComments, setApprovalComments] = useState('')
  const [rejectionReason, setRejectionReason] = useState('')
  const [isApproving, setIsApproving] = useState(false)
  const [isRejecting, setIsRejecting] = useState(false)

  if (!period) return null

  const handleApprove = async () => {
    setIsApproving(true)
    try {
      // TODO: Implement actual approval API call
      onApprove(period)
      onClose()
    } catch (error) {
      console.error('Error approving payroll:', error)
    } finally {
      setIsApproving(false)
    }
  }

  const handleReject = async () => {
    if (!rejectionReason.trim()) {
      alert('Please provide a reason for rejection')
      return
    }
    
    setIsRejecting(true)
    try {
      // TODO: Implement actual rejection API call
      onReject(period)
      onClose()
    } catch (error) {
      console.error('Error rejecting payroll:', error)
    } finally {
      setIsRejecting(false)
    }
  }

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(amount)
  }

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl w-[90vw] max-h-[90vh] overflow-hidden flex flex-col p-0">
        {/* Header */}
        <div className="px-6 py-4 border-b bg-gradient-to-r from-yellow-50 to-orange-50">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className="p-2 bg-yellow-100 rounded-lg">
                <CheckCircle className="h-5 w-5 text-yellow-600" />
              </div>
              <div>
                <DialogTitle className="text-xl font-semibold text-slate-800">
                  Payroll Approval - {period.periodName}
                </DialogTitle>
                <DialogDescription className="text-slate-600">
                  Review and approve payroll for {new Date(period.payDate).toLocaleDateString('en-US', {
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric'
                  })}
                </DialogDescription>
              </div>
            </div>
            <Button
              variant="ghost"
              size="sm"
              onClick={onClose}
              className="text-slate-500 hover:text-slate-700"
            >
              <XCircle className="h-5 w-5" />
            </Button>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-hidden p-6">
          <div className="h-full flex flex-col space-y-6">
            {/* Payroll Summary */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
              <div className="bg-gradient-to-br from-green-50 to-emerald-50 rounded-lg p-4 border border-green-200">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs font-medium text-green-700 uppercase tracking-wide">Total Gross Pay</p>
                    <p className="text-xl font-bold text-green-600">{formatCurrency(period.totalGrossPay)}</p>
                  </div>
                  <DollarSign className="h-6 w-6 text-green-500" />
                </div>
              </div>
              <div className="bg-gradient-to-br from-blue-50 to-cyan-50 rounded-lg p-4 border border-blue-200">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs font-medium text-blue-700 uppercase tracking-wide">Total Net Pay</p>
                    <p className="text-xl font-bold text-blue-600">{formatCurrency(period.totalNetPay)}</p>
                  </div>
                  <Calculator className="h-6 w-6 text-blue-500" />
                </div>
              </div>
              <div className="bg-gradient-to-br from-red-50 to-rose-50 rounded-lg p-4 border border-red-200">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs font-medium text-red-700 uppercase tracking-wide">Total Taxes</p>
                    <p className="text-xl font-bold text-red-600">{formatCurrency(period.totalTaxes)}</p>
                  </div>
                  <Shield className="h-6 w-6 text-red-500" />
                </div>
              </div>
              <div className="bg-gradient-to-br from-purple-50 to-violet-50 rounded-lg p-4 border border-purple-200">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs font-medium text-purple-700 uppercase tracking-wide">Employee Count</p>
                    <p className="text-xl font-bold text-purple-600">{period._count?.payrollRecords || 0}</p>
                  </div>
                  <Users className="h-6 w-6 text-purple-500" />
                </div>
              </div>
            </div>

            {/* Approval Form */}
            <div className="bg-white rounded-lg border border-slate-200 p-6">
              <h3 className="text-lg font-semibold text-slate-800 mb-4">Approval Details</h3>
              
              <div className="space-y-4">
                <div>
                  <Label htmlFor="approval-comments" className="text-sm font-medium text-slate-700">
                    Approval Comments (Optional)
                  </Label>
                  <textarea
                    id="approval-comments"
                    value={approvalComments}
                    onChange={(e) => setApprovalComments(e.target.value)}
                    placeholder="Add any comments about this payroll approval..."
                    className="w-full mt-1 px-3 py-2 border border-slate-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    rows={3}
                  />
                </div>

                <div>
                  <Label htmlFor="rejection-reason" className="text-sm font-medium text-slate-700">
                    Rejection Reason (If Rejecting)
                  </Label>
                  <textarea
                    id="rejection-reason"
                    value={rejectionReason}
                    onChange={(e) => setRejectionReason(e.target.value)}
                    placeholder="Provide a reason for rejection..."
                    className="w-full mt-1 px-3 py-2 border border-slate-300 rounded-md focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent"
                    rows={3}
                  />
                </div>
              </div>
            </div>

            {/* Approval History */}
            <div className="bg-white rounded-lg border border-slate-200 p-6">
              <h3 className="text-lg font-semibold text-slate-800 mb-4">Approval History</h3>
              <div className="space-y-3">
                <div className="flex items-center space-x-3 p-3 bg-slate-50 rounded-lg">
                  <div className="p-2 bg-blue-100 rounded-full">
                    <Clock className="h-4 w-4 text-blue-600" />
                  </div>
                  <div className="flex-1">
                    <p className="text-sm font-medium text-slate-900">Submitted for Approval</p>
                    <p className="text-xs text-slate-500">By: John Doe • {new Date().toLocaleDateString()}</p>
                  </div>
                </div>
                <div className="flex items-center space-x-3 p-3 bg-slate-50 rounded-lg">
                  <div className="p-2 bg-green-100 rounded-full">
                    <CheckCircle className="h-4 w-4 text-green-600" />
                  </div>
                  <div className="flex-1">
                    <p className="text-sm font-medium text-slate-900">Payroll Processed</p>
                    <p className="text-xs text-slate-500">By: System • {new Date().toLocaleDateString()}</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t bg-gradient-to-r from-slate-50 to-yellow-50">
          <div className="flex items-center justify-between">
            <div className="text-sm text-slate-600">
              Review all details before approving
            </div>
            <div className="flex items-center space-x-3">
              <Button
                variant="outline"
                size="sm"
                onClick={onClose}
                className="border-slate-300 text-slate-700 hover:bg-slate-50"
              >
                Cancel
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={handleReject}
                disabled={isRejecting}
                className="border-red-300 text-red-700 hover:bg-red-50"
              >
                {isRejecting ? 'Rejecting...' : 'Reject'}
              </Button>
              <Button
                size="sm"
                onClick={handleApprove}
                disabled={isApproving}
                className="bg-green-600 hover:bg-green-700 text-white"
              >
                {isApproving ? 'Approving...' : 'Approve Payroll'}
              </Button>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  )
}
